import type { Address } from "viem"
import type { Config } from "wagmi"
import { formatUnits } from "viem"

import type { ApprovalMode, ContractRiskLevel, EmitEvent, Nft, Token } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import type { ManageLockLensResult } from "@/domains/on-chain"
import { determineApprovalAmount, EventEmitter, makeApprovalDescription, makeGroupId } from "@/domains/library"
import { LockAmountIncreasedEvent } from "@/domains/lock/events"
import { BIGINT_ZERO } from "@/domains/on-chain"
import { TokenStore } from "@/domains/tokens"
import { TokenApprovalCompletedEvent } from "@/domains/tokens/events"

import type { IIncreaseLockAmountActions } from "./interfaces"

/* eslint-disable */

interface IncreaseLockAmountInteractionParams {
  account: Address
  chainId: number
  config: Config
  nft: Nft
  amount: bigint
  vestedToken: Token
  governanceToken: TokenStore
  manageLockLens: ManageLockLensResult
  approvalMode: ApprovalMode
  contractRiskLevel: ContractRiskLevel
  emitter: EventEmitter<EmitEvent>
  eventBus: IEventBus
  onChainActions: IIncreaseLockAmountActions
}

export const increaseLockAmountInteraction = async ({
  account,
  chainId,
  config,
  nft,
  amount,
  vestedToken,
  governanceToken,
  manageLockLens,
  approvalMode,
  contractRiskLevel,
  emitter,
  eventBus,
  onChainActions,
}: IncreaseLockAmountInteractionParams) => {
  // Generate a unique group ID for this interaction instance
  const groupId = makeGroupId("increase-lock-amount")

  // 1. Does the user have enough funds in wallet to lock?
  if (manageLockLens.accountBalanceToken.wei < amount) {
    emitter.emit("error-increasing-lock-duration", {
      title: "Insufficient balance",
      description: `You do not have enough tokens to lock for NFT #${nft.id}`,
      variant: "error",
    })
    return false
  }

  let didUserSuccessfullyApprove = false

  // 2. Does the user have enough allowance to lock?
  const allowance = manageLockLens.vestedSpendTokenAllowance.wei ?? BIGINT_ZERO
  if (allowance < amount) {
    const actualAmountToApprove = determineApprovalAmount(amount, approvalMode, contractRiskLevel)
    emitter.emit("approve-token-allowance", {
      title: "Approving spending tokens",
      description: makeApprovalDescription(actualAmountToApprove, vestedToken.decimals),
      variant: "info",
    })

    const approval = await onChainActions.approveTokenAllowance({
      account,
      chainId,
      config,
      contractAddress: governanceToken.address.toViemAddress(),
      spenderAddress: vestedToken.address,
      spenderAmount: amount,
      approvalMode,
      contractRiskLevel,
    })

    if (approval.success) {
      eventBus.publish(
        new TokenApprovalCompletedEvent(
          groupId,
          governanceToken.address.toViemAddress(),
          vestedToken.address,
          amount,
          approval.tx,
        ),
      )
      emitter.emit("approve-token-allowance", {
        title: "Approval successful",
        description: "You are ready to increase lock",
        variant: "success",
      })
      didUserSuccessfullyApprove = true
    }

    if (approval.success === false) {
      emitter.emit("approve-token-allowance-error", {
        title: "Unable to approve spending tokens",
        description: approval.error,
        variant: "error",
      })
      return false
    }
  }

  // 3. Increase lock amount
  // only show this if they didn't approve, we don't want a double popup
  if (!didUserSuccessfullyApprove) {
    emitter.emit("increasing-lock-amount", {
      title: "Increase lock amount",
      description: `Increasing lock amount by ${formatUnits(amount, governanceToken.decimals)} ${vestedToken.symbol}`,
      variant: "info",
    })
  }

  const result = await onChainActions.increaseLockAmount({
    account,
    chainId,
    config,
    vestedAddress: vestedToken.address,
    tokenId: nft.id,
    amount,
  })

  if (result.success) {
    eventBus.publish(new LockAmountIncreasedEvent(groupId, nft.id, amount, result.tx))
    emitter.emit("increase-lock-amount", {
      title: "Increase lock amount",
      description: `NFT #${nft.id} lock amount increased`,
      variant: "success",
    })
    return result
  }

  if (result.success === false) {
    emitter.emit("error-increasing-lock-amount", {
      title: "Increase lock amount failed",
      description: `Unable to increase lock amount for NFT #${nft.id}, reason ${result.error}`,
      variant: "error",
    })
    return false
  }

  return result
}
